import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { v4 as uuid } from "uuid";
import WorkbenchNotesService, {
    NoteSave,
    WorkbenchNotesView,
} from "../services/WorkbenchNotesService";
import { ResponseErr } from "../../common/services/AppClient";
import type ErrorStore from "../../common/stores/ErrorStore";

export const WORKBENCH_NOTE_MAX_LENGTH = 2000;

const errorText = (error: ResponseErr, fallback: string) => {
    const title = error.data?.title;
    return typeof title === "string" &&
        title.trim().length > 0 &&
        title.length <= 300 &&
        !title.trimStart().startsWith("<")
        ? title
        : fallback;
};

export default class WorkbenchNotesStore {
    view: WorkbenchNotesView | null = null;
    loading = false;
    loadError = "";
    open = false;
    saving = false;
    draft = "";
    saveError = "";
    opening = false;
    pending: NoteSave | null = null;
    private formContext: Pick<
        WorkbenchNotesView,
        "submissionId" | "submissionIdentifier"
    > | null = null;
    private readVersion = 0;
    private read: Promise<boolean> | null = null;

    constructor(
        private service: WorkbenchNotesService,
        readonly subjectId: string,
        private errorStore: ErrorStore,
    ) {
        makeObservable<this, "formContext">(this, {
            view: observable.ref,
            loading: observable,
            loadError: observable,
            open: observable,
            saving: observable,
            draft: observable,
            saveError: observable,
            opening: observable,
            pending: observable.ref,
            formContext: observable.ref,
            count: computed,
            valid: computed,
            recovering: computed,
            draftLocked: computed,
            needsContextReview: computed,
            formSubmissionIdentifier: computed,
            setDraft: action.bound,
            refresh: action.bound,
            start: action.bound,
            reviewContext: action.bound,
            cancel: action.bound,
            save: action.bound,
        });
    }

    get count() {
        return this.view?.notes.length ?? 0;
    }

    get valid() {
        const text = this.draft.trim();
        return text.length > 0 && text.length <= WORKBENCH_NOTE_MAX_LENGTH;
    }

    get recovering() {
        return this.pending !== null && !this.saving;
    }

    get draftLocked() {
        return this.saving || this.pending !== null;
    }

    get needsContextReview() {
        return this.open && this.formContext === null;
    }

    get formSubmissionIdentifier() {
        return this.formContext?.submissionIdentifier ?? null;
    }

    setDraft(value: string) {
        if (!this.draftLocked) this.draft = value;
    }

    refresh(): Promise<boolean> {
        if (this.read) return this.read;
        const version = ++this.readVersion;
        this.loading = true;
        const read = this.loadNotes(version);
        this.read = read;
        void read.finally(() => {
            if (this.read === read) this.read = null;
        });
        return read;
    }

    private async loadNotes(version: number) {
        try {
            const result = await this.service.get(this.subjectId);
            return runInAction(() => {
                if (version !== this.readVersion) return false;
                this.loading = false;
                if (result.isErr()) {
                    this.loadError = errorText(
                        result.error,
                        "Could not load notes. Try again.",
                    );
                    if ([403, 404].includes(result.error.status))
                        this.view = null;
                    return false;
                }
                this.view = result.value;
                this.loadError = "";
                return true;
            });
        } catch {
            return runInAction(() => {
                if (version === this.readVersion) {
                    this.loading = false;
                    this.loadError = "Could not load notes. Try again.";
                }
                return false;
            });
        }
    }

    async start() {
        if (this.opening || this.open) return;
        this.opening = true;
        try {
            if (!(await this.reviewContext())) {
                void this.errorStore.addBasicError(
                    new Error(
                        this.loadError ||
                            "Could not open the note dialog. Try again.",
                    ),
                );
                return;
            }
            runInAction(() => {
                this.draft = "";
                this.open = true;
            });
        } finally {
            runInAction(() => {
                this.opening = false;
            });
        }
    }

    async reviewContext() {
        if (this.saving || this.pending) return false;
        if (!(await this.refresh())) return false;
        return runInAction(() => {
            this.formContext = {
                submissionId: this.view!.submissionId,
                submissionIdentifier: this.view!.submissionIdentifier,
            };
            this.saveError = "";
            return true;
        });
    }

    cancel() {
        if (this.saving) return;
        const uncertain = this.pending !== null;
        this.pending = null;
        this.open = false;
        this.draft = "";
        if (uncertain) void this.refresh();
    }

    async save() {
        if (this.saving || !this.open || !this.valid || !this.formContext)
            return false;
        this.pending ??= {
            requestId: uuid(),
            text: this.draft.trim(),
            expectedSubmissionId: this.formContext.submissionId,
        };
        const payload = this.pending;
        this.saving = true;
        this.saveError = "";
        try {
            const result = await this.service.create(this.subjectId, payload);
            runInAction(() => {
                if (result.isErr()) {
                    this.saveError = errorText(
                        result.error,
                        "Could not save the note. Retry save to recover the result.",
                    );
                    const status = result.error.status;
                    if (
                        status >= 400 &&
                        status < 500 &&
                        ![401, 403, 404, 408, 429].includes(status)
                    ) {
                        this.pending = null;
                    }
                    if (status === 409) this.formContext = null;
                    return;
                }
                // An older in-flight list must not erase the successful save.
                ++this.readVersion;
                this.loading = false;
                // Only a fresh server read establishes the current owner's list.
                this.read = null;
                this.view = null;
                this.pending = null;
                this.open = false;
                this.draft = "";
            });
            if (result.isOk()) {
                await this.refresh();
                return true;
            }
            return false;
        } catch {
            runInAction(() => {
                this.saveError =
                    "Could not confirm the save. Retry save to recover the result.";
            });
            return false;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    }
}
